import React from 'react';
import { Check, CircleAlert } from 'lucide-react';
import { Spinner } from '../ui';

export type GenerationStatus = 'idle' | 'generating' | 'complete' | 'error';

export type StepState = 'done' | 'active' | 'error' | 'todo';

export function stepState(stepNumber: number, currentStep: number, status: GenerationStatus): StepState {
  if (status === 'error' && stepNumber === currentStep) return 'error';
  if (stepNumber < currentStep || status === 'complete') return 'done';
  if (stepNumber === currentStep) return status === 'generating' ? 'active' : 'todo';
  return 'todo';
}

/**
 * The progress list both generators show: one row per server step, with the
 * server's own status line under the step that is running (which provider,
 * how many sources, how many items so far).
 */
export const GenerationSteps: React.FC<{
  labels: string[];
  currentStep: number;
  status: GenerationStatus;
  message?: string | null;
}> = ({ labels, currentStep, status, message }) => (
  <ol className="flex-shrink-0 px-5 pb-3 pt-3" aria-label="Progress">
    {labels.map((label, index) => {
      const state = stepState(index + 1, currentStep, status);
      const showMessage = !!message && (state === 'active' || state === 'error');
      return (
        <li key={label} aria-current={state === 'active' ? 'step' : undefined} className="text-[13px]">
          <div className="flex h-7 items-center gap-2.5">
            <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
              {state === 'done' && <Check className="h-4 w-4 text-emerald-600" strokeWidth={2} />}
              {state === 'active' && <Spinner className="h-3.5 w-3.5 text-ink" />}
              {state === 'error' && <CircleAlert className="h-4 w-4 text-primary-600" strokeWidth={1.75} />}
              {state === 'todo' && <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" />}
            </span>
            <span
              className={
                state === 'error'
                  ? 'text-primary-700'
                  : state === 'active'
                    ? 'text-ink'
                    : state === 'done'
                      ? 'text-neutral-500'
                      : 'text-neutral-400'
              }
            >
              {label}
            </span>
          </div>
          {showMessage && (
            <p className="-mt-1 mb-1 pl-[26px] text-[12px] leading-snug text-neutral-400" aria-live="polite">
              {message}
            </p>
          )}
        </li>
      );
    })}
  </ol>
);
